// Material take-off for a route: for each stop whose lead is Converted, the
// Accepted quote(s) that match that stop's address, plus totals across the route.
import { useState, useEffect, useMemo } from 'react';
import { getLeadEstimates } from '../leads/leadService';

const isConverted = (stop) => String(stop?.status?.status_name || '').toLowerCase() === 'converted';

// Quote address and stop address use the same convention: null = lead's primary.
const sameAddress = (est, stop) => (est.leadAddressId ?? null) === (stop.leadAddressId ?? null);

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

export const buildMaterials = (stops, estimatesByLead) => {
  const stopCards = stops.filter(isConverted).map((stop) => {
    const quotes = (estimatesByLead[stop.leadId] || [])
      .filter(e => e.status === 'Accepted' && sameAddress(e, stop));
    return { stop, quotes };
  });

  const totals = new Map();
  let grandTotal = 0;
  stopCards.forEach(({ quotes }) => quotes.forEach((q) => {
    grandTotal += num(q.totalPrice);
    (q.items || []).forEach((it) => {
      const unit = it.measurementUnit || 'sqft';
      const name = it.Service?.service_name || it.observation || 'Item';
      const key = `${it.serviceId ?? name}|${unit}`;
      const row = totals.get(key) || { key, name, unit, qty: 0, total: 0 };
      row.qty += num(it.totalArea);
      row.total += num(it.totalPrice);
      totals.set(key, row);
    });
  }));

  return {
    stopCards,
    summary: [...totals.values()].sort((a, b) => a.name.localeCompare(b.name)),
    grandTotal,
  };
};

const useRouteMaterials = (stops) => {
  const [estimatesByLead, setEstimatesByLead] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Refetch only when the set of converted leads changes.
  const leadKey = useMemo(
    () => [...new Set(stops.filter(isConverted).map(s => s.leadId))].sort().join(','),
    [stops],
  );

  useEffect(() => {
    const ids = leadKey ? leadKey.split(',') : [];
    if (!ids.length) { setEstimatesByLead({}); setError(''); return undefined; }
    let cancelled = false;
    setLoading(true);
    setError('');
    Promise.allSettled(ids.map(id => getLeadEstimates(id)))
      .then((results) => {
        if (cancelled) return;
        const map = {};
        let failed = 0;
        results.forEach((r, i) => {
          if (r.status === 'fulfilled') map[ids[i]] = Array.isArray(r.value.data) ? r.value.data : [];
          else failed += 1;
        });
        setEstimatesByLead(map);
        if (failed) setError(`Could not load quotes for ${failed} lead(s). Totals may be incomplete.`);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [leadKey]);

  const data = useMemo(() => buildMaterials(stops, estimatesByLead), [stops, estimatesByLead]);
  return { ...data, loading, error };
};

export default useRouteMaterials;
